import { LDRecord } from '@launchdarkly/session-replay';
import type { LDClient } from 'launchdarkly-js-client-sdk';

let GLOBAL_LD_CLIENT: LDClient | undefined;

/**
 * Lightweight holder for the LaunchDarkly client.
 *
 * This module is intentionally dependency-light (no React, no import of the LD
 * provider) so the hot request-header path can read flag variations without
 * pulling in the heavy provider module — and without creating an import cycle
 * (http.ts → launch-darkly.tsx → useUser → http.ts).
 *
 * `setLDClient` is called once the React provider has the client; `getLDClient`
 * is read by non-React code such as the outbound request-header builder.
 */
export const setLDClient = (client: LDClient | undefined) => {
  GLOBAL_LD_CLIENT = client;
};

export const getLDClient = () => GLOBAL_LD_CLIENT;

/**
 * Reads the LaunchDarkly session-replay URL for the current session.
 *
 * `LDRecord.getSession()` is synchronous, worker-free and never throws, so this
 * is safe to call from the request-header path. We intentionally do NOT call
 * any recording-initiating API (`LDRecord.start()` / `LDObserve.start()`) here.
 * Errors are swallowed → null so a header read can never break a request.
 */
export function getLDSessionReplayUrl(): string | null {
  try {
    return LDRecord.getSession()?.urlWithTimestamp ?? null;
  } catch {
    return null;
  }
}
